import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, Trash2, X, Clock, Save,
  CalendarDays, CalendarRange, RefreshCw, AlertTriangle, CheckCircle2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_LABEL = { QV: 'Quick Visit', HH: 'Half Hour', HR: 'Full Hour' };
const SERVICE_DURATION = { QV: 15, HH: 30, HR: 60 };

const STATUS_BADGE = {
  pending: 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/30',
  confirmed: 'bg-green-500/15 text-green-300 border border-green-500/30',
  completed: 'bg-blue-500/15 text-blue-300 border border-blue-500/30',
  cancelled: 'bg-red-500/15 text-red-300 border border-red-500/30',
};

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function authHeaders() {
  const token = localStorage.getItem('casa_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function authedFetch(path, init = {}) {
  return fetch(`${API_URL}${path}`, {
    ...init,
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
}

// Local YYYY-MM-DD helper
const toDateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateLong = (d) =>
  d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });


export function SchedulePanel() {
  const [view, setView] = useState('today'); // today | week | month
  const [anchor, setAnchor] = useState(() => new Date());
  const [appts, setAppts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creatingBlock, setCreatingBlock] = useState(false);
  const [openAppt, setOpenAppt] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const range = useMemo(() => computeRange(view, anchor), [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start: range.startISO,
        end: range.endISO,
      });
      const [schedRes, settingsRes] = await Promise.all([
        authedFetch(`/api/admin/schedule?${params}`),
        authedFetch('/api/admin/schedule/settings'),
      ]);
      if (schedRes.ok) {
        const data = await schedRes.json();
        setAppts(data.appointments || []);
        setBlocks(data.blocks || []);
      }
      if (settingsRes.ok) setSettings(await settingsRes.json());
    } catch (_e) {
      setError('Could not load schedule.');
    }
    setLoading(false);
  }, [range.startISO, range.endISO]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this appointment?')) return;
    const res = await authedFetch(`/api/admin/appointments/${id}/cancel`, { method: 'POST' });
    if (res.ok) {
      setOpenAppt(null);
      load();
    }
  };

  const handleReschedule = async (id, newStartISO, durationMin) => {
    const res = await authedFetch(`/api/admin/appointments/${id}/reschedule`, {
      method: 'PATCH',
      body: JSON.stringify({ start_at: newStartISO, duration_min: durationMin }),
    });
    if (res.ok) {
      setOpenAppt(null);
      load();
    } else {
      const data = await res.json().catch(() => ({}));
      alert(data.detail || 'Could not reschedule.');
    }
  };

  const handleDeleteBlock = async (id) => {
    const res = await authedFetch(`/api/admin/schedule/blocks/${id}`, { method: 'DELETE' });
    if (res.ok) load();
  };

  return (
    <div className="space-y-4" data-testid="schedule-panel">
      <Toolbar
        view={view}
        setView={setView}
        anchor={anchor}
        setAnchor={setAnchor}
        range={range}
        loading={loading}
        onRefresh={load}
        onNewBlock={() => setCreatingBlock(true)}
        onSettings={() => setShowSettings(true)}
      />

      {error && (
        <div className="rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 px-3 py-2 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <SummaryStrip appts={appts} blocks={blocks} />

      {view === 'today' && (
        <DayView
          day={anchor}
          appts={appts}
          blocks={blocks}
          onApptClick={setOpenAppt}
          onBlockDelete={handleDeleteBlock}
        />
      )}
      {view === 'week' && (
        <WeekView
          range={range}
          appts={appts}
          blocks={blocks}
          onApptClick={setOpenAppt}
          onDayClick={(d) => { setAnchor(d); setView('today'); }}
        />
      )}
      {view === 'month' && (
        <MonthView
          range={range}
          appts={appts}
          blocks={blocks}
          onDayClick={(d) => { setAnchor(d); setView('today'); }}
        />
      )}

      {creatingBlock && (
        <BlockEditor
          defaultDate={anchor}
          onClose={() => setCreatingBlock(false)}
          onSaved={() => { setCreatingBlock(false); load(); }}
        />
      )}

      {openAppt && (
        <AppointmentEditor
          appt={openAppt}
          onClose={() => setOpenAppt(null)}
          onCancel={() => handleCancel(openAppt.id)}
          onReschedule={(iso, dur) => handleReschedule(openAppt.id, iso, dur)}
        />
      )}

      {showSettings && settings && (
        <SettingsEditor
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSaved={() => { setShowSettings(false); load(); }}
        />
      )}
    </div>
  );
}


// --- Toolbar -------------------------------------------------------
function Toolbar({ view, setView, anchor, setAnchor, range, loading, onRefresh, onNewBlock, onSettings }) {
  const stepDays = view === 'today' ? 1 : view === 'week' ? 7 : 30;
  const move = (delta) => {
    const next = new Date(anchor);
    next.setDate(next.getDate() + delta * stepDays);
    setAnchor(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 justify-between bg-[#111022] border border-white/10 rounded-xl p-3">
      <div className="flex items-center gap-1 bg-[#0B0A12] border border-white/5 rounded-lg p-1">
        {[
          { key: 'today', label: 'Today', Icon: Calendar },
          { key: 'week', label: 'Week', Icon: CalendarRange },
          { key: 'month', label: 'Month', Icon: CalendarDays },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`px-3 py-1.5 text-xs rounded-md flex items-center gap-1.5 transition-colors ${
              view === key ? 'bg-[#FF4DA6] text-white' : 'text-[#A7A3BA] hover:text-[#F4F2FF]'
            }`}
            data-testid={`schedule-view-${key}`}
          >
            <Icon className="w-3 h-3" /> {label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => move(-1)} className="p-1.5 rounded-md bg-[#0B0A12] text-[#A7A3BA] hover:text-[#FFB6D8]" data-testid="schedule-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="text-sm text-[#F4F2FF] min-w-[180px] text-center" data-testid="schedule-range-label">
          {range.label}
        </div>
        <button onClick={() => move(1)} className="p-1.5 rounded-md bg-[#0B0A12] text-[#A7A3BA] hover:text-[#FFB6D8]" data-testid="schedule-next">
          <ChevronRight className="w-4 h-4" />
        </button>
        <button onClick={() => setAnchor(new Date())} className="text-xs px-3 py-1.5 rounded-md bg-[#27D7FF]/15 text-[#27D7FF] hover:bg-[#27D7FF]/25" data-testid="schedule-jump-today">
          Today
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={onRefresh} className="p-1.5 rounded-md bg-[#0B0A12] text-[#A7A3BA] hover:text-[#F4F2FF]" data-testid="schedule-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button onClick={onNewBlock} className="text-xs px-3 py-1.5 rounded-md bg-[#8B5CFF] text-white flex items-center gap-1" data-testid="schedule-new-block">
          <Plus className="w-3 h-3" /> Block time
        </button>
        <button onClick={onSettings} className="text-xs px-3 py-1.5 rounded-md bg-[#0B0A12] text-[#A7A3BA] border border-white/5 hover:text-[#F4F2FF]" data-testid="schedule-settings-open">
          Settings
        </button>
      </div>
    </div>
  );
}


function SummaryStrip({ appts, blocks }) {
  const stats = useMemo(() => {
    const counts = { pending: 0, confirmed: 0, completed: 0, cancelled: 0 };
    appts.forEach((a) => { counts[a.status] = (counts[a.status] || 0) + 1; });
    return counts;
  }, [appts]);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {[
        { label: 'Pending', val: stats.pending, color: 'text-yellow-300' },
        { label: 'Confirmed', val: stats.confirmed, color: 'text-green-300' },
        { label: 'Completed', val: stats.completed, color: 'text-blue-300' },
        { label: 'Cancelled', val: stats.cancelled, color: 'text-red-300' },
        { label: 'Blocks', val: blocks.length, color: 'text-purple-300' },
      ].map(({ label, val, color }) => (
        <div key={label} className="bg-[#111022] border border-white/10 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#A7A3BA]">{label}</p>
          <p className={`text-xl font-semibold ${color}`}>{val}</p>
        </div>
      ))}
    </div>
  );
}


// --- Day view ------------------------------------------------------
function DayView({ day, appts, blocks, onApptClick, onBlockDelete }) {
  const items = useMemo(() => {
    const merged = [
      ...appts.map((a) => ({ kind: 'appt', start: a.start_at, end: a.end_at, data: a })),
      ...blocks.map((b) => ({ kind: 'block', start: b.start_at, end: b.end_at, data: b })),
    ];
    merged.sort((a, b) => a.start.localeCompare(b.start));
    return merged;
  }, [appts, blocks]);

  return (
    <div className="bg-[#111022] border border-white/10 rounded-xl p-4">
      <p className="text-sm text-[#A7A3BA] mb-3">{day.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      {items.length === 0 ? (
        <p className="text-sm text-[#A7A3BA] py-4 text-center">No appointments or blocks for this day.</p>
      ) : (
        <ul className="divide-y divide-white/5" data-testid="day-list">
          {items.map((item, i) => (
            <li key={i} className="py-2 flex items-center gap-3">
              <Clock className="w-4 h-4 text-[#A7A3BA] flex-shrink-0" />
              <span className="text-sm text-[#F4F2FF] w-32 flex-shrink-0">
                {formatTime(item.start)} – {formatTime(item.end)}
              </span>
              {item.kind === 'appt' ? (
                <>
                  <button
                    onClick={() => onApptClick(item.data)}
                    className="text-sm text-[#F4F2FF] hover:text-[#FFB6D8] text-left flex-1"
                    data-testid={`appt-${item.data.id}`}
                  >
                    {item.data.name} <span className="text-[#A7A3BA]">· {SERVICE_LABEL[item.data.service_code] || item.data.service_code}</span>
                  </button>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_BADGE[item.data.status] || ''}`}>
                    {item.data.status}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-sm text-[#A7A3BA] flex-1 italic">
                    Blocked{item.data.reason ? ` — ${item.data.reason}` : ''}
                  </span>
                  <button onClick={() => onBlockDelete(item.data.id)} className="text-[#A7A3BA] hover:text-red-300 p-1" data-testid={`block-delete-${item.data.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// --- Week view -----------------------------------------------------
function WeekView({ range, appts, blocks, onApptClick, onDayClick }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="week-grid">
      {range.days.map((d) => {
        const key = toDateKey(d);
        const dayAppts = appts.filter((a) => a.start_at.startsWith(key));
        const dayBlocks = blocks.filter((b) => b.start_at.startsWith(key) || (b.start_at < key && b.end_at > key));
        return (
          <div key={key} className="bg-[#111022] border border-white/10 rounded-lg p-3 min-h-[160px] flex flex-col">
            <button onClick={() => onDayClick(d)} className="text-left">
              <p className="text-[11px] uppercase tracking-wider text-[#A7A3BA]">{WEEKDAY_LABELS[(d.getDay() + 6) % 7]}</p>
              <p className="text-sm font-semibold text-[#F4F2FF]">{d.getDate()}</p>
            </button>
            <div className="mt-2 space-y-1 flex-1 overflow-y-auto">
              {dayAppts.map((a) => (
                <button
                  key={a.id}
                  onClick={() => onApptClick(a)}
                  className="w-full text-left text-[11px] px-2 py-1 rounded bg-[#FF4DA6]/15 text-[#FFB6D8] hover:bg-[#FF4DA6]/25 truncate"
                  title={`${a.name} · ${SERVICE_LABEL[a.service_code]}`}
                  data-testid={`week-appt-${a.id}`}
                >
                  {formatTime(a.start_at)} {a.name}
                </button>
              ))}
              {dayBlocks.map((b) => (
                <div key={b.id} className="text-[11px] px-2 py-1 rounded bg-purple-500/15 text-purple-300 truncate italic">
                  Blocked
                </div>
              ))}
              {dayAppts.length === 0 && dayBlocks.length === 0 && (
                <p className="text-[11px] text-[#A7A3BA]/60 text-center py-2">free</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// --- Month view ----------------------------------------------------
function MonthView({ range, appts, blocks, onDayClick }) {
  return (
    <div className="bg-[#111022] border border-white/10 rounded-xl p-3" data-testid="month-grid">
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-[#A7A3BA] mb-2">
        {WEEKDAY_LABELS.map((d) => (
          <div key={d} className="text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {range.days.map((d) => {
          const key = toDateKey(d);
          const dayAppts = appts.filter((a) => a.start_at.startsWith(key));
          const dayBlocks = blocks.filter((b) => b.start_at.startsWith(key));
          const muted = d.getMonth() !== range.anchorMonth;
          return (
            <button
              key={key}
              onClick={() => onDayClick(d)}
              className={`rounded-lg p-2 min-h-[70px] text-left border transition-colors ${
                muted
                  ? 'border-white/5 bg-[#0B0A12] text-[#A7A3BA]/40'
                  : 'border-white/10 bg-[#0B0A12] text-[#F4F2FF] hover:border-[#FF4DA6]/40'
              }`}
              data-testid={`month-day-${key}`}
            >
              <p className="text-xs font-semibold">{d.getDate()}</p>
              <div className="mt-1 flex flex-wrap gap-0.5">
                {dayAppts.slice(0, 3).map((a) => (
                  <span key={a.id} className="w-1.5 h-1.5 rounded-full bg-[#FF4DA6]" />
                ))}
                {dayBlocks.slice(0, 2).map((b) => (
                  <span key={b.id} className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                ))}
                {dayAppts.length > 3 && (
                  <span className="text-[9px] text-[#A7A3BA]">+{dayAppts.length - 3}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// --- Block creator -------------------------------------------------
function BlockEditor({ defaultDate, onClose, onSaved }) {
  const dateStr = toDateKey(defaultDate);
  const [date, setDate] = useState(dateStr);
  const [startTime, setStartTime] = useState('14:00');
  const [endTime, setEndTime] = useState('16:00');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    const startIso = new Date(`${date}T${startTime}`).toISOString();
    const endIso = new Date(`${date}T${endTime}`).toISOString();
    if (new Date(endIso) <= new Date(startIso)) {
      setErr('End must be after start.');
      setSaving(false);
      return;
    }
    const res = await authedFetch('/api/admin/schedule/blocks', {
      method: 'POST',
      body: JSON.stringify({ start_at: startIso, end_at: endIso, reason }),
    });
    if (res.ok) onSaved();
    else setErr('Could not create block.');
    setSaving(false);
  };

  return (
    <Modal title="Block time" onClose={onClose} testId="block-editor">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Date">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="form-input" data-testid="block-date" required />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start"><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="form-input" data-testid="block-start" required /></Field>
          <Field label="End"><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="form-input" data-testid="block-end" required /></Field>
        </div>
        <Field label="Reason (optional)">
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="form-input" placeholder="Personal day, training, etc." data-testid="block-reason" />
        </Field>
        {err && <p className="text-red-300 text-sm">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[#A7A3BA] hover:text-[#F4F2FF]">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-[#8B5CFF] text-white text-sm disabled:opacity-50" data-testid="block-save">{saving ? 'Saving…' : 'Block time'}</button>
        </div>
      </form>
    </Modal>
  );
}


// --- Appointment editor (reschedule + cancel) ----------------------
function AppointmentEditor({ appt, onClose, onCancel, onReschedule }) {
  const [date, setDate] = useState(toDateKey(new Date(appt.start_at)));
  const start = new Date(appt.start_at);
  const [time, setTime] = useState(`${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`);
  const [duration, setDuration] = useState(appt.duration_min || SERVICE_DURATION[appt.service_code] || 30);

  const submit = (e) => {
    e.preventDefault();
    const iso = new Date(`${date}T${time}`).toISOString();
    onReschedule(iso, Number(duration));
  };

  return (
    <Modal title="Appointment details" onClose={onClose} testId="appt-editor">
      <div className="space-y-3 text-sm">
        <div><span className="text-[#A7A3BA]">Client:</span> <span className="text-[#F4F2FF] font-medium">{appt.name}</span></div>
        <div><span className="text-[#A7A3BA]">Phone:</span> <span className="text-[#F4F2FF]">{appt.phone || '—'}</span></div>
        <div><span className="text-[#A7A3BA]">Service:</span> <span className="text-[#F4F2FF]">{SERVICE_LABEL[appt.service_code] || appt.service_code}</span></div>
        <div><span className="text-[#A7A3BA]">Status:</span> <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_BADGE[appt.status] || ''}`}>{appt.status}</span></div>
        {appt.notes && <div><span className="text-[#A7A3BA]">Notes:</span> <span className="text-[#F4F2FF]">{appt.notes}</span></div>}

        <form onSubmit={submit} className="border-t border-white/5 pt-3 space-y-3">
          <p className="text-xs uppercase tracking-wider text-[#A7A3BA]">Reschedule</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="form-input" data-testid="appt-date" /></Field>
            <Field label="Start"><input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="form-input" data-testid="appt-time" /></Field>
          </div>
          <Field label="Duration (min)">
            <input type="number" min="15" max="240" step="15" value={duration} onChange={(e) => setDuration(e.target.value)} className="form-input" data-testid="appt-duration" />
          </Field>
          <div className="flex justify-between items-center pt-2">
            <button type="button" onClick={onCancel} className="px-3 py-2 rounded-lg bg-red-500/15 text-red-300 text-xs hover:bg-red-500/25" data-testid="appt-cancel">Cancel appointment</button>
            <button type="submit" className="px-4 py-2 rounded-lg bg-[#27D7FF] text-[#07060B] text-sm font-medium" data-testid="appt-save">Save reschedule</button>
          </div>
        </form>
      </div>
    </Modal>
  );
}


// --- Settings editor ----------------------------------------------
function SettingsEditor({ settings, onClose, onSaved }) {
  const [bufferBefore, setBufferBefore] = useState(settings.buffer_before_min);
  const [bufferAfter, setBufferAfter] = useState(settings.buffer_after_min);
  const [step, setStep] = useState(settings.slot_step_min);
  const [hours, setHours] = useState({ ...settings.business_hours });
  const [saving, setSaving] = useState(false);

  const updateHour = (day, key, value) => {
    setHours((h) => ({ ...h, [day]: { ...h[day], [key]: value } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await authedFetch('/api/admin/schedule/settings', {
      method: 'PUT',
      body: JSON.stringify({
        buffer_before_min: Number(bufferBefore),
        buffer_after_min: Number(bufferAfter),
        slot_step_min: Number(step),
        business_hours: hours,
      }),
    });
    setSaving(false);
    if (res.ok) onSaved();
  };

  return (
    <Modal title="Schedule settings" onClose={onClose} testId="settings-editor">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Buffer before (min)">
            <input type="number" min="0" max="60" value={bufferBefore} onChange={(e) => setBufferBefore(e.target.value)} className="form-input" data-testid="settings-buffer-before" />
          </Field>
          <Field label="Buffer after (min)">
            <input type="number" min="0" max="60" value={bufferAfter} onChange={(e) => setBufferAfter(e.target.value)} className="form-input" data-testid="settings-buffer-after" />
          </Field>
          <Field label="Slot step (min)">
            <input type="number" min="5" max="60" value={step} onChange={(e) => setStep(e.target.value)} className="form-input" data-testid="settings-step" />
          </Field>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-[#A7A3BA] mb-2">Business hours</p>
          <div className="space-y-2">
            {WEEKDAY_LABELS.map((label, i) => {
              const day = String(i);
              const cfg = hours[day] || { open: '10:00', close: '23:00', closed: false };
              return (
                <div key={day} className="flex items-center gap-3 bg-[#0B0A12] border border-white/5 rounded-lg p-2">
                  <span className="text-sm text-[#F4F2FF] w-12">{label}</span>
                  <label className="flex items-center gap-2 text-xs text-[#A7A3BA]">
                    <input type="checkbox" checked={!cfg.closed} onChange={(e) => updateHour(day, 'closed', !e.target.checked)} className="accent-[#FF4DA6]" data-testid={`hours-${day}-open`} />
                    Open
                  </label>
                  <input type="time" value={cfg.open} onChange={(e) => updateHour(day, 'open', e.target.value)} disabled={cfg.closed} className="form-input flex-1 disabled:opacity-40" data-testid={`hours-${day}-from`} />
                  <span className="text-[#A7A3BA] text-xs">→</span>
                  <input type="time" value={cfg.close} onChange={(e) => updateHour(day, 'close', e.target.value)} disabled={cfg.closed} className="form-input flex-1 disabled:opacity-40" data-testid={`hours-${day}-to`} />
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[#A7A3BA] hover:text-[#F4F2FF]">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-[#27D7FF] text-[#07060B] text-sm font-medium flex items-center gap-1" data-testid="settings-save">
            <Save className="w-3 h-3" /> {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </form>
    </Modal>
  );
}


// --- Tiny shared bits ----------------------------------------------
function Modal({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid={testId}>
      <div className="bg-[#111022] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <h3 className="text-[#F4F2FF] font-semibold">{title}</h3>
          <button onClick={onClose} className="text-[#A7A3BA] hover:text-[#F4F2FF]" data-testid={`${testId}-close`}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#A7A3BA] block mb-1">{label}</span>
      {children}
    </label>
  );
}


// --- Range computation (UTC start/end + ISO strings) ---------------
function computeRange(view, anchor) {
  if (view === 'today') {
    const start = new Date(anchor);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return {
      startISO: start.toISOString(),
      endISO: end.toISOString(),
      label: formatDateLong(anchor),
      days: [new Date(anchor)],
      anchorMonth: anchor.getMonth(),
    };
  }
  if (view === 'week') {
    const start = new Date(anchor);
    const dow = (start.getDay() + 6) % 7; // Mon = 0
    start.setDate(start.getDate() - dow);
    start.setHours(0, 0, 0, 0);
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
    const end = new Date(days[6]);
    end.setDate(end.getDate() + 1);
    return {
      startISO: start.toISOString(),
      endISO: end.toISOString(),
      label: `${formatDateLong(days[0])} – ${formatDateLong(days[6])}`,
      days,
      anchorMonth: anchor.getMonth(),
    };
  }
  // month
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const dowFirst = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(start.getDate() - dowFirst);
  const totalCells = Math.ceil((dowFirst + last.getDate()) / 7) * 7;
  const days = Array.from({ length: totalCells }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
  const end = new Date(days[days.length - 1]);
  end.setDate(end.getDate() + 1);
  end.setHours(0, 0, 0, 0);
  return {
    startISO: start.toISOString(),
    endISO: end.toISOString(),
    label: anchor.toLocaleDateString([], { month: 'long', year: 'numeric' }),
    days,
    anchorMonth: anchor.getMonth(),
  };
}


export default SchedulePanel;
